<template>
  <v-dialog v-model="dialogVisible" max-width="700px" persistent>
    <v-card class="onboard-card">
      <v-toolbar class="toolbar-color">
        <v-toolbar-title class="text-white">
          <v-icon color="white">mdi-account-plus</v-icon>
          Onboard New Team
        </v-toolbar-title>
        <v-btn icon @click="closeDialog" class="close-btn" elevation="0">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="dialog-content">
        <v-form ref="form" @submit.prevent="handleSubmit">
          <!-- Organization Details Section -->
          <div class="form-section mb-8">
            <div class="section-header mb-6">
              <v-icon size="24" color="primary" class="mr-3">mdi-domain</v-icon>
              <h3 class="text-h6 font-weight-bold">Organization Details</h3>
            </div>

            <v-text-field
              v-model="formData.organizationName"
              label="Organization Name *"
              variant="outlined"
              required
              :rules="[
                (v) => !!v || 'Organization name is required',
                (v) =>
                  v.length >= 3 ||
                  'Organization name must be at least 3 characters',
              ]"
              class="form-field"
              prepend-inner-icon="mdi-office-building"
              @input="onOrganizationNameChange"
            />

            <v-text-field
              v-model="formData.organizationTeamId"
              label="Organization Team ID * (Auto-generated)"
              variant="outlined"
              required
              maxlength="5"
              counter="5"
              :rules="[(v) => !!v || 'Team ID is required']"
              class="form-field"
              prepend-inner-icon="mdi-identifier"
              :disabled="!showTeamIdField || teamIdValidating"
              :readonly="false"
              hint="First 5 letters of organization name in uppercase (max 5 characters)"
              persistent-hint
              :error="!!teamIdError"
              :error-messages="teamIdError"
              :success="teamIdValid && !teamIdError"
              :success-messages="
                teamIdValid && !teamIdError ? 'Team ID is available' : ''
              "
              @input="onTeamIdChange"
              @blur="verifyTeamId"
            >
              <template v-slot:append>
                <v-icon
                  v-if="teamIdValidating"
                  class="rotating"
                  color="primary"
                >
                  mdi-loading
                </v-icon>
                <v-icon v-else-if="teamIdValid" color="success">
                  mdi-check-circle
                </v-icon>
                <v-icon v-else-if="teamIdError" color="error">
                  mdi-alert-circle
                </v-icon>
              </template>
            </v-text-field>
          </div>

          <!-- Account Owner Details Section -->
          <div
            class="form-section mb-8"
            :class="{ 'disabled-section': !showAccountOwnerSection }"
          >
            <div class="section-header mb-6">
              <v-icon size="24" color="primary" class="mr-3"
                >mdi-account-circle</v-icon
              >
              <h3 class="text-h6 font-weight-bold">Account Owner Details</h3>
            </div>

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.firstName"
                  label="First Name *"
                  variant="outlined"
                  required
                  :rules="[(v) => !!v || 'First name is required']"
                  class="form-field"
                  prepend-inner-icon="mdi-account"
                  :disabled="!showAccountOwnerSection"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.lastName"
                  label="Last Name"
                  variant="outlined"
                  class="form-field"
                  prepend-inner-icon="mdi-account"
                  :disabled="!showAccountOwnerSection"
                />
              </v-col>
            </v-row>

            <v-text-field
              v-model="formData.email"
              label="Email ID *"
              type="email"
              variant="outlined"
              required
              :rules="[
                (v) => !!v || 'Email is required',
                (v) =>
                  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Email must be valid',
              ]"
              class="form-field"
              prepend-inner-icon="mdi-email"
              :disabled="!showAccountOwnerSection"
            />

            <v-select
              v-model="formData.country"
              :items="countries"
              label="Country"
              variant="outlined"
              class="form-field"
              prepend-inner-icon="mdi-earth"
              :disabled="!showAccountOwnerSection"
            />

            <v-row>
              <v-col cols="12" md="4">
                <v-select
                  v-model="formData.countryCode"
                  :items="countryCodes"
                  label="Country Code"
                  variant="outlined"
                  class="form-field"
                  prepend-inner-icon="mdi-phone"
                  :disabled="!showAccountOwnerSection"
                />
              </v-col>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="formData.phoneNumber"
                  label="Phone Number"
                  variant="outlined"
                  class="form-field"
                  prepend-inner-icon="mdi-phone"
                  :disabled="!showAccountOwnerSection"
                />
              </v-col>
            </v-row>
          </div>
        </v-form>
      </v-card-text>

      <v-card-actions class="dialog-footer">
        <v-btn
          color="primary"
          @click="handleSubmit"
          :loading="submitting"
          :disabled="!isFormValid"
          class="action-btn"
          prepend-icon="mdi-check"
          size="large"
        >
          Onboard Team
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";

export default {
  name: "OnboardDialog",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      submitting: false,
      showTeamIdField: false,
      showAccountOwnerSection: false,
      teamIdValidating: false,
      teamIdValid: false,
      teamIdError: "",
      formData: {
        organizationName: "",
        organizationTeamId: "",
        firstName: "",
        lastName: "",
        email: "",
        country: "India",
        countryCode: "India(+91)",
        phoneNumber: "",
      },
      countries: [
        "India",
        "United States",
        "United Kingdom",
        "Canada",
        "Australia",
        "Germany",
        "France",
        "Japan",
        "China",
        "Brazil",
      ],
      countryCodes: [
        "India(+91)",
        "United States(+1)",
        "United Kingdom(+44)",
        "Canada(+1)",
        "Australia(+61)",
        "Germany(+49)",
        "France(+33)",
        "Japan(+81)",
        "China(+86)",
        "Brazil(+55)",
      ],
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    isFormValid() {
      return (
        this.formData.organizationName &&
        this.formData.organizationName.length >= 5 &&
        this.formData.organizationTeamId &&
        this.teamIdValid &&
        this.formData.firstName &&
        this.formData.email
      );
    },
  },
  watch: {
    "formData.organizationTeamId"(newValue) {
      // console.log("Organization Team ID changed:", newValue);

      // Show Account Owner section when team ID is filled and valid
      this.showAccountOwnerSection =
        this.showTeamIdField && newValue && this.teamIdValid;
    },
  },

  methods: {
    onOrganizationNameChange() {
      // Show Team ID field when organization name has at least 5 characters
      this.showTeamIdField = this.formData.organizationName.length >= 5;

      // Auto-generate Team ID from first 5 letters of organization name
      if (this.formData.organizationName.length >= 5) {
        this.formData.organizationTeamId = this.formData.organizationName
          .substring(0, 5)
          .toUpperCase();
        // Validate the new team ID
        this.verifyTeamId();
      } else {
        this.formData.organizationTeamId = "";
        this.teamIdValid = false;
        this.teamIdError = "";
      }

      // Show Account Owner section when both organization name and team ID are filled
      this.showAccountOwnerSection =
        this.showTeamIdField &&
        this.formData.organizationTeamId &&
        this.teamIdValid;
    },

    async verifyTeamId() {
      if (!this.formData.organizationTeamId) return;

      this.teamIdValidating = true;
      this.teamIdValid = false;
      this.teamIdError = "";

      try {
        const response = await axios({
          method: "POST",
          url: "https://sn3ezxtgcc.execute-api.us-east-1.amazonaws.com/dev/check_organization_team_id",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": "EgnUv9Owyt8KacFkfnB44as9uqCT8ZaF9FZ1cVU6",
          },
          data: {
            organization_team_id: this.formData.organizationTeamId,
          },
        });

        if (response.data.Status === "SUCCESS") {
          this.teamIdValid = true;
          this.teamIdError = "";
          // Update Account Owner section visibility
          this.showAccountOwnerSection =
            this.showTeamIdField &&
            this.formData.organizationTeamId &&
            this.teamIdValid;
          this.$store.dispatch("setNotification", {
            type: "success",
            message: response.data.Message || "Team ID is available",
          });
        } else {
          this.teamIdValid = false;
          this.teamIdError = response.data.Message || "Team ID already exists";
          this.$store.dispatch("setNotification", {
            type: "error",
            message: response.data.Message || "Team ID already exists",
          });
        }
      } catch (error) {
        this.teamIdValid = false;
        this.teamIdError = "Failed to validate Team ID";
        this.$store.dispatch("setNotification", {
          type: "error",
          message: "Failed to validate Team ID. Please try again.",
        });
      } finally {
        this.teamIdValidating = false;
      }
    },

    onTeamIdChange() {
      // Convert to uppercase
      this.formData.organizationTeamId =
        this.formData.organizationTeamId.toUpperCase();

      // Reset validation when user manually changes the team ID
      this.teamIdValid = false;
      this.teamIdError = "";
      this.showAccountOwnerSection = false;
    },

    async handleSubmit() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.submitting = true;
      try {
        // Call the create organization API
        const response = await this.createUser();

        if (response.data.Status === "SUCCESS") {
          // Create new team object for local state

          // Show success notification
          this.$store.dispatch("setNotification", {
            type: "success",
            message:
              response.data.Message || "Organization created successfully",
          });

          // Emit success event

          // Close dialog
          this.closeDialog();
        } else {
          this.$store.dispatch("setNotification", {
            type: "error",
            message: response.data.Message || "Failed to create organization",
          });
        }
      } catch (error) {
        console.error("Error creating organization:", error);
        this.$store.dispatch("setNotification", {
          type: "error",
          message: "Failed to create organization. Please try again.",
        });
      } finally {
        this.submitting = false;
      }
    },

    async createUser() {
      return await axios({
        method: "POST",
        url: "https://sn3ezxtgcc.execute-api.us-east-1.amazonaws.com/dev/create_oragnization",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "EgnUv9Owyt8KacFkfnB44as9uqCT8ZaF9FZ1cVU6",
        },
        data: {
          user_email_id: this.formData.email,
          partner_id: this.$store.state.partnerDetails.partner_id,
          organization_name: this.formData.organizationName,
          first_name: this.formData.firstName,
          last_name:
            this.formData.lastName === "" ? undefined : this.formData.lastName,
          user_contact_number:
            this.formData.phoneNumber === ""
              ? undefined
              : this.formData.phoneNumber,
          user_country_code:
            this.formData.phoneNumber === ""
              ? undefined
              : this.formData.countryCode,
          organization_team_id: this.formData.organizationTeamId,
          organization_country: this.formData.country,
        },
      });
    },

    closeDialog() {
      this.dialogVisible = false;
      this.resetForm();
    },

    resetForm() {
      this.formData = {
        organizationName: "",
        organizationTeamId: "",
        firstName: "",
        lastName: "",
        email: "",
        country: "India",
        countryCode: "India(+91)",
        phoneNumber: "",
      };
      this.showTeamIdField = false;
      this.showAccountOwnerSection = false;
      this.teamIdValidating = false;
      this.teamIdValid = false;
      this.teamIdError = "";
      this.$refs.form?.resetValidation();
    },
  },
};
</script>

<style scoped>
.toolbar-color {
  background: linear-gradient(135deg, #3f78e0 0%, #da4c77 100%) !important;
}
.onboard-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.dialog-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 24px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.close-btn {
  color: white !important;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.close-btn:hover {
  opacity: 1;
}

.dialog-content {
  padding: 32px;
  background-color: #fafbfc;
}

.form-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e9ecef;
}

.section-header {
  display: flex;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 2px solid #f1f3f4;
  margin-bottom: 24px;
}

.section-header h3 {
  color: #2c3e50;
  margin: 0;
  font-size: 1.25rem;
}

.form-field {
  margin-bottom: 20px;
}

.form-field :deep(.v-field) {
  border-radius: 8px;
  transition: all 0.2s ease;
}

.form-field :deep(.v-field:hover) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-field :deep(.v-field--focused) {
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

.dialog-footer {
  background: white;
  padding: 24px 32px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.action-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.5px;
  border-radius: 8px;
  padding: 12px 24px;
  transition: all 0.2s ease;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .dialog-content {
    padding: 24px 20px;
  }

  .form-section {
    padding: 20px;
  }

  .dialog-footer {
    padding: 20px;
    flex-direction: column;
    gap: 12px;
  }

  .action-btn {
    width: 100%;
  }
}

/* Animation for form sections */
.form-section {
  animation: slideInUp 0.3s ease-out;
}

/* Disabled section styling */
.disabled-section {
  opacity: 0.6;
  pointer-events: none;
  position: relative;
}

.disabled-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  z-index: 1;
  border-radius: 12px;
}

.disabled-section .section-header {
  position: relative;
  z-index: 2;
}

.disabled-section .form-field {
  position: relative;
  z-index: 2;
}

/* Rotating loading icon */
.rotating {
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Custom scrollbar for the dialog */
.dialog-content {
  max-height: 70vh;
  overflow-y: auto;
}

.dialog-content::-webkit-scrollbar {
  width: 6px;
}

.dialog-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.dialog-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.dialog-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
